import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Skeleton } from '@/components/ui/skeleton'
import { OnboardingService } from '@/services/onboarding.service'
import { useAuth } from '@/auth/Auth'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import AchAccountForm from './account-types/ach'
import SwiftAccountForm from './account-types/swift'
import VirtualAccountForm from './account-types/virtual'
import WireAccountForm from './account-types/wire'

export default function InternationalAccount({
  mode,
  canEdit = true,
  isOnboarding = false,
}: {
  mode: 'create' | 'edit'
  canEdit?: boolean
  isOnboarding?: boolean
}) {
  const { t } = useTranslation('onboarding')
  const { authData } = useAuth()

  const accountTypeOptions = [
    { label: t('ACH'), value: 'ACH' },
    { label: t('WIRE'), value: 'WIRE' },
    { label: t('SWIFT'), value: 'SWIFT' },
    { label: t('VIRTUAL'), value: 'VIRTUAL' },
  ]

  const { data: bank, isLoading } = useQuery({
    queryKey: ['list-usd-banks', authData?.id],
    queryFn: async () => {
      const response = await OnboardingService.getInternationalBankAccounts()
      return response?.data?.[0]
    },
    enabled: Boolean(authData?.id),
  })

  const [accountType, setAccountType] = React.useState<string>('')
  const [userSelected, setUserSelected] = React.useState<boolean>(false)
  const previousUserIdRef = useRef<string | number | undefined>(undefined)

  useEffect(() => {
    const userId = authData?.id
    if (previousUserIdRef.current === userId) return
    previousUserIdRef.current = userId
    setAccountType('')
    setUserSelected(false)
  }, [authData?.id])

  useEffect(() => {
    if (!userSelected && bank?.rail) {
      setAccountType(bank.rail)
    }
  }, [bank, userSelected])
  const handleAccountTypeChange = (value: string) => {
    setAccountType(value)
    setUserSelected(true)
  }

  if (isLoading) {
    return (
      <div className="mt-6 space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-4">
      <CustomSelect
        label={t('account_type')}
        placeholder={t('select_account_type')}
        value={accountType}
        options={accountTypeOptions}
        onValueChange={handleAccountTypeChange}
        className="col-span-2"
      />
      {accountType === 'ACH' && (
        <AchAccountForm
          requiredFields={[
            'account_holder_name',
            'account_holder_type',
            'routing_number',
            'account_number',
            'account_class',
            'bank_name',
            'bank_country',
            'bank_address',
          ]}
          mode={bank?.rail === 'ACH' && bank.id ? 'edit' : 'create'}
          bank={bank?.rail === 'ACH' ? bank : undefined}
          canEdit={canEdit}
          isOnboarding={isOnboarding}
        />
      )}
      {accountType === 'WIRE' && (
        <WireAccountForm
          requiredFields={[
            'account_holder_name',
            'account_holder_type',
            'routing_number',
            'account_number',
            'account_class',
            'bank_name',
            'bank_country',
            'bank_address',
          ]}
          mode={bank?.rail === 'WIRE' && bank.id ? 'edit' : 'create'}
          bank={bank?.rail === 'WIRE' ? bank : undefined}
          canEdit={canEdit}
          isOnboarding={isOnboarding}
        />
      )}
      {accountType === 'SWIFT' && (
        <SwiftAccountForm
          requiredFields={[
            'account_holder_name',
            'account_holder_type',
            'account_number',
            'bank_name',
            'bank_country',
            'bank_address',
            'iban',
            'swift_code',
          ]}
          rail={'SWIFT'}
          mode={bank?.rail === 'SWIFT' && bank.id ? 'edit' : 'create'}
          bank={bank?.rail === 'SWIFT' ? bank : undefined}
          canEdit={canEdit}
          isOnboarding={isOnboarding}
        />
      )}
      {accountType === 'VIRTUAL' && (
        <VirtualAccountForm
          requiredFields={[
            'account_holder_name',
            'account_holder_type',
            'routing_number',
            'account_number',
            'account_class',
            'bank_name',
            'bank_country',
            'bank_address',
            'provider',
            'virtual_account_id',
            'payoneer_email',
          ]}
          rail={'VIRTUAL'}
          mode={bank?.rail === 'VIRTUAL' && bank.id ? 'edit' : 'create'}
          bank={bank?.rail === 'VIRTUAL' ? bank : undefined}
          canEdit={canEdit}
          isOnboarding={isOnboarding}
        />
      )}
    </div>
  )
}